import { AlertCircle, Loader2 } from "lucide-react";
import type { ReactNode } from "react";

/** Placeholder while a screen's data loads. */
export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div
      className="flex items-center justify-center gap-2 py-16 text-sm"
      style={{ color: "var(--muted-foreground)" }}
      role="status"
    >
      <Loader2 className="size-4 animate-spin" aria-hidden />
      <span>{label}</span>
    </div>
  );
}

/** Something went wrong, with a way back. */
export function ErrorState({
  title = "Something went wrong",
  description,
  onRetry,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <span
        className="flex size-12 items-center justify-center rounded-full"
        style={{
          background: "color-mix(in oklch, var(--destructive) 12%, var(--background))",
          color: "var(--destructive-text, var(--destructive))",
        }}
      >
        <AlertCircle className="size-6" aria-hidden />
      </span>
      <div className="text-base font-semibold" style={{ color: "var(--foreground)" }}>
        {title}
      </div>
      {description && (
        <p className="max-w-md text-sm" style={{ color: "var(--muted-foreground)" }}>
          {description}
        </p>
      )}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 h-9 cursor-pointer rounded-md border px-4 text-sm font-medium"
          style={{ borderColor: "var(--border)", color: "var(--foreground)" }}
        >
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * Nothing to show yet.
 *
 * Says what will appear rather than that nothing is here — the house voice, and
 * more useful to someone looking at a screen for the first time.
 */
export function EmptyState({
  icon,
  title,
  description,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-14 text-center">
      {icon && (
        <span
          className="mb-1 flex size-12 items-center justify-center rounded-full"
          style={{ background: "var(--muted)", color: "var(--muted-foreground)" }}
        >
          {icon}
        </span>
      )}
      <div className="text-sm font-semibold" style={{ color: "var(--foreground)" }}>
        {title}
      </div>
      {description && (
        <p className="max-w-sm text-sm" style={{ color: "var(--muted-foreground)" }}>
          {description}
        </p>
      )}
    </div>
  );
}
